import { BACKEND_URL } from '@/lib/backendUrl';
/**
 * Serviço de Banco de Dados Local - IndexedDB
 * Permite que o PDV funcione offline armazenando dados localmente
 */

const DB_NAME = 'nfcesyspdv_offline';
const DB_VERSION = 1;

// Stores (tabelas) do banco local
const STORES = {
  PRODUCTS: 'products',
  CUSTOMERS: 'customers',
  OFFLINE_SALES: 'offline_sales',
  CONFIG: 'config',
  SYNC_QUEUE: 'sync_queue',
};

class OfflineDatabase {
  constructor() {
    this.db = null;
    this.isOnline = navigator.onLine;
    this.syncInProgress = false;
    
    // Monitorar status de conexão
    window.addEventListener('online', () => {
      this.isOnline = true;
      console.log('[OfflineDB] Online - iniciando sincronização...');
      this.syncWithServer();
    });
    
    window.addEventListener('offline', () => {
      this.isOnline = false;
      console.log('[OfflineDB] Offline - usando dados locais');
    });
  }

  /**
   * Inicializa o banco de dados IndexedDB
   */
  async init() {
    return new Promise((resolve, reject) => {
      const request = indexedDB.open(DB_NAME, DB_VERSION);

      request.onerror = () => {
        console.error('[OfflineDB] Erro ao abrir banco:', request.error);
        reject(request.error);
      };

      request.onsuccess = () => {
        this.db = request.result;
        console.log('[OfflineDB] Banco de dados inicializado');
        resolve(this.db);
      };

      request.onupgradeneeded = (event) => {
        const db = event.target.result;

        // Store de Produtos
        if (!db.objectStoreNames.contains(STORES.PRODUCTS)) {
          const productsStore = db.createObjectStore(STORES.PRODUCTS, { keyPath: 'id' });
          productsStore.createIndex('barcode', 'barcode', { unique: false });
          productsStore.createIndex('name', 'name', { unique: false });
          productsStore.createIndex('category', 'category', { unique: false });
        }

        // Store de Clientes
        if (!db.objectStoreNames.contains(STORES.CUSTOMERS)) {
          const customersStore = db.createObjectStore(STORES.CUSTOMERS, { keyPath: 'id' });
          customersStore.createIndex('cpf_cnpj', 'cpf_cnpj', { unique: false });
          customersStore.createIndex('name', 'name', { unique: false });
        }

        // Store de Vendas Offline (para sincronizar depois)
        if (!db.objectStoreNames.contains(STORES.OFFLINE_SALES)) {
          const salesStore = db.createObjectStore(STORES.OFFLINE_SALES, { keyPath: 'local_id', autoIncrement: true });
          salesStore.createIndex('created_at', 'created_at', { unique: false });
          salesStore.createIndex('synced', 'synced', { unique: false });
        }

        // Store de Configurações
        if (!db.objectStoreNames.contains(STORES.CONFIG)) {
          db.createObjectStore(STORES.CONFIG, { keyPath: 'key' });
        }

        // Store de Fila de Sincronização
        if (!db.objectStoreNames.contains(STORES.SYNC_QUEUE)) {
          const syncStore = db.createObjectStore(STORES.SYNC_QUEUE, { keyPath: 'id', autoIncrement: true });
          syncStore.createIndex('type', 'type', { unique: false });
          syncStore.createIndex('created_at', 'created_at', { unique: false });
        }

        console.log('[OfflineDB] Estrutura do banco criada/atualizada');
      };
    });
  }

  /**
   * Executa uma transação no banco
   */
  async transaction(storeName, mode = 'readonly') {
    if (!this.db) await this.init();
    return this.db.transaction(storeName, mode).objectStore(storeName);
  }

  // ==========================================
  // PRODUTOS
  // ==========================================

  /**
   * Salva produtos no banco local
   */
  async saveProducts(products) {
    const store = await this.transaction(STORES.PRODUCTS, 'readwrite');
    
    return new Promise((resolve, reject) => {
      let count = 0;
      
      products.forEach(product => {
        const request = store.put(product);
        request.onsuccess = () => {
          count++;
          if (count === products.length) {
            console.log(`[OfflineDB] ${count} produtos salvos localmente`);
            resolve(count);
          }
        };
        request.onerror = () => reject(request.error);
      });
      
      if (products.length === 0) resolve(0);
    });
  }

  /**
   * Busca todos os produtos locais
   */
  async getProducts() {
    const store = await this.transaction(STORES.PRODUCTS);
    
    return new Promise((resolve, reject) => {
      const request = store.getAll();
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  }

  /**
   * Busca produto por código de barras
   */
  async getProductByBarcode(barcode) {
    const store = await this.transaction(STORES.PRODUCTS);
    const index = store.index('barcode');
    
    return new Promise((resolve, reject) => {
      const request = index.get(barcode);
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  }

  /**
   * Busca produto por ID
   */
  async getProductById(id) {
    const store = await this.transaction(STORES.PRODUCTS);
    
    return new Promise((resolve, reject) => {
      const request = store.get(id);
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  }

  /**
   * Busca produtos por nome (parcial)
   */
  async searchProducts(query) {
    const products = await this.getProducts();
    const searchLower = query.toLowerCase();
    
    return products.filter(p => 
      p.name?.toLowerCase().includes(searchLower) ||
      p.barcode?.includes(query) ||
      p.id?.includes(query)
    );
  }

  // ==========================================
  // CLIENTES
  // ==========================================

  /**
   * Salva clientes no banco local
   */
  async saveCustomers(customers) {
    const store = await this.transaction(STORES.CUSTOMERS, 'readwrite');
    
    return new Promise((resolve, reject) => {
      let count = 0;
      
      customers.forEach(customer => {
        const request = store.put(customer);
        request.onsuccess = () => {
          count++;
          if (count === customers.length) {
            console.log(`[OfflineDB] ${count} clientes salvos localmente`);
            resolve(count);
          }
        };
        request.onerror = () => reject(request.error);
      });
      
      if (customers.length === 0) resolve(0);
    });
  }

  /**
   * Busca todos os clientes locais
   */
  async getCustomers() {
    const store = await this.transaction(STORES.CUSTOMERS);
    
    return new Promise((resolve, reject) => {
      const request = store.getAll();
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  }

  // ==========================================
  // VENDAS OFFLINE
  // ==========================================

  /**
   * Salva venda offline (para sincronizar depois)
   */
  async saveOfflineSale(sale) {
    const store = await this.transaction(STORES.OFFLINE_SALES, 'readwrite');
    
    const offlineSale = {
      ...sale,
      created_at: new Date().toISOString(),
      synced: false,
      sync_attempts: 0,
    };
    
    return new Promise((resolve, reject) => {
      const request = store.add(offlineSale);
      request.onsuccess = () => {
        console.log('[OfflineDB] Venda salva offline:', request.result);
        resolve(request.result);
      };
      request.onerror = () => reject(request.error);
    });
  }

  /**
   * Busca vendas offline não sincronizadas
   */
  async getOfflineSales() {
    const store = await this.transaction(STORES.OFFLINE_SALES);
    
    return new Promise((resolve, reject) => {
      const request = store.getAll();
      request.onsuccess = () => {
        const unsyncedSales = request.result.filter(s => !s.synced);
        resolve(unsyncedSales);
      };
      request.onerror = () => reject(request.error);
    });
  }

  /**
   * Marca venda como sincronizada
   */
  async markSaleSynced(localId, serverId) {
    const store = await this.transaction(STORES.OFFLINE_SALES, 'readwrite');
    
    return new Promise((resolve, reject) => {
      const getRequest = store.get(localId);
      
      getRequest.onsuccess = () => {
        const sale = getRequest.result;
        if (sale) {
          sale.synced = true;
          sale.server_id = serverId;
          sale.synced_at = new Date().toISOString();
          
          const updateRequest = store.put(sale);
          updateRequest.onsuccess = () => resolve(true);
          updateRequest.onerror = () => reject(updateRequest.error);
        } else {
          resolve(false);
        }
      };
      
      getRequest.onerror = () => reject(getRequest.error);
    });
  }

  /**
   * Conta vendas pendentes de sincronização
   */
  async countPendingSales() {
    const sales = await this.getOfflineSales();
    return sales.length;
  }

  // ==========================================
  // CONFIGURAÇÕES
  // ==========================================

  /**
   * Salva configuração local
   */
  async setConfig(key, value) {
    const store = await this.transaction(STORES.CONFIG, 'readwrite');
    
    return new Promise((resolve, reject) => {
      const request = store.put({ key, value, updated_at: new Date().toISOString() });
      request.onsuccess = () => resolve(true);
      request.onerror = () => reject(request.error);
    });
  }

  /**
   * Busca configuração local
   */
  async getConfig(key) {
    const store = await this.transaction(STORES.CONFIG);
    
    return new Promise((resolve, reject) => {
      const request = store.get(key);
      request.onsuccess = () => resolve(request.result?.value);
      request.onerror = () => reject(request.error);
    });
  }

  // ==========================================
  // SINCRONIZAÇÃO
  // ==========================================

  /**
   * Sincroniza dados com o servidor
   */
  async syncWithServer() {
    if (this.syncInProgress || !this.isOnline) return;
    
    this.syncInProgress = true;
    console.log('[OfflineDB] Iniciando sincronização...');
    
    try {
      // 1. Sincronizar vendas offline
      await this.syncOfflineSales();
      
      // 2. Atualizar produtos do servidor
      await this.syncProducts();
      
      // 3. Atualizar clientes do servidor
      await this.syncCustomers();
      
      // Salvar última sincronização
      await this.setConfig('last_sync', new Date().toISOString());
      
      console.log('[OfflineDB] Sincronização concluída!');
    } catch (error) {
      console.error('[OfflineDB] Erro na sincronização:', error);
    } finally {
      this.syncInProgress = false;
    }
  }

  /**
   * Sincroniza vendas offline com o servidor
   */
  async syncOfflineSales() {
    const pendingSales = await this.getOfflineSales();
    
    if (pendingSales.length === 0) {
      console.log('[OfflineDB] Nenhuma venda pendente');
      return;
    }
    
    console.log(`[OfflineDB] Sincronizando ${pendingSales.length} venda(s)...`);
    
    const token = localStorage.getItem('token');
    if (!token) {
      console.warn('[OfflineDB] Token não encontrado, pulando sync de vendas');
      return;
    }
    
    const API_URL = BACKEND_URL;
    
    for (const sale of pendingSales) {
      try {
        const response = await fetch(`${API_URL}/api/sales`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
          },
          body: JSON.stringify({
            ...sale,
            offline_id: sale.local_id,
            synced_from_offline: true,
          }),
        });
        
        if (response.ok) {
          const data = await response.json();
          await this.markSaleSynced(sale.local_id, data.sale?.id);
          console.log(`[OfflineDB] Venda ${sale.local_id} sincronizada`);
        } else {
          console.error(`[OfflineDB] Erro ao sincronizar venda ${sale.local_id}`);
        }
      } catch (error) {
        console.error(`[OfflineDB] Erro ao sincronizar venda ${sale.local_id}:`, error);
      }
    }
  }

  /**
   * Sincroniza produtos do servidor para local
   */
  async syncProducts() {
    const token = localStorage.getItem('token');
    if (!token) return;
    
    const API_URL = BACKEND_URL;
    
    try {
      const response = await fetch(`${API_URL}/api/products`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      
      if (response.ok) {
        const data = await response.json();
        const products = data.products || data || [];
        await this.saveProducts(products);
      }
    } catch (error) {
      console.error('[OfflineDB] Erro ao sincronizar produtos:', error);
    }
  }

  /**
   * Sincroniza clientes do servidor para local
   */
  async syncCustomers() {
    const token = localStorage.getItem('token');
    if (!token) return;
    
    const API_URL = BACKEND_URL;
    
    try {
      const response = await fetch(`${API_URL}/api/customers`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      
      if (response.ok) {
        const data = await response.json();
        const customers = data.customers || data || [];
        await this.saveCustomers(customers);
      }
    } catch (error) {
      console.error('[OfflineDB] Erro ao sincronizar clientes:', error);
    }
  }

  /**
   * Força sincronização manual
   */
  async forceSync() {
    if (!this.isOnline) {
      throw new Error('Sem conexão com a internet');
    }
    await this.syncWithServer();
  }

  /**
   * Limpa todos os dados locais
   */
  async clearAllData() {
    const stores = [STORES.PRODUCTS, STORES.CUSTOMERS, STORES.CONFIG];
    
    for (const storeName of stores) {
      const store = await this.transaction(storeName, 'readwrite');
      await new Promise((resolve, reject) => {
        const request = store.clear();
        request.onsuccess = () => resolve();
        request.onerror = () => reject(request.error);
      });
    }
    
    console.log('[OfflineDB] Dados locais limpos');
  }

  /**
   * Retorna estatísticas do banco local
   */
  async getStats() {
    const products = await this.getProducts();
    const customers = await this.getCustomers();
    const pendingSales = await this.getOfflineSales();
    const lastSync = await this.getConfig('last_sync');
    
    return {
      products_count: products.length,
      customers_count: customers.length,
      pending_sales: pendingSales.length,
      last_sync: lastSync,
      is_online: this.isOnline,
    };
  }
}

// Singleton
const offlineDB = new OfflineDatabase();

export default offlineDB;
export { STORES };
